import React, { useState } from 'react';
import { Modal, Form, Input, Select, Button, message, Space, Row, Col } from 'antd';
import { crmCreateLead, crmCreateDeal } from '../api';
import { stripDuplicateCountryCode, autoDetectCountryCode } from '../utils/phone';

interface QuickCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (clientId: number, dealId: number, isReturning?: boolean) => void;
}

const LEAD_SOURCE_OPTIONS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'referral', label: 'Referral' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'website', label: 'Website' },
  { value: 'event', label: 'Event' },
  { value: 'klaviyo', label: 'Klaviyo' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'phone_call', label: 'Phone Call' },
  { value: 'other', label: 'Other' },
];

const BUDGET_RANGE_OPTIONS = [
  { value: 'Under 50,000 EGP', label: 'Under 50,000 EGP' },
  { value: '50,000 – 100,000 EGP', label: '50,000 – 100,000 EGP' },
  { value: '100,000 – 250,000 EGP', label: '100,000 – 250,000 EGP' },
  { value: '250,000 – 500,000 EGP', label: '250,000 – 500,000 EGP' },
  { value: '500,000+ EGP', label: '500,000+ EGP' },
  { value: 'To be confirmed', label: 'To be confirmed' },
];

const PRODUCT_CATEGORY_OPTIONS = [
  { value: 'couture_bridal', label: 'Couture Bridal (CB)' },
  { value: 'couture_soiree', label: 'Couture Soiree (CS)' },
  { value: 'rtw_bridal', label: 'RTW Bridal (RB)' },
  { value: 'rtw_soiree', label: 'RTW Soiree (RS)' },
  { value: 'showroom', label: 'Showroom (SH)' },
  { value: 'collection_couture', label: 'Collection Couture (CC)' },
  { value: 'collection_rtw', label: 'Collection RTW (CR)' },
  { value: 'product_design', label: 'Product Design (PD)' },
  { value: 'veil', label: 'Veil (VL)' },
];

// Country code prefix options
const COUNTRY_CODE_OPTIONS = [
  { value: '+20', label: '+20 EG' },
  { value: '+971', label: '+971 AE' },
  { value: '+966', label: '+966 SA' },
  { value: '+974', label: '+974 QA' },
  { value: '+965', label: '+965 KW' },
  { value: '+973', label: '+973 BH' },
  { value: '+968', label: '+968 OM' },
  { value: '+44', label: '+44 GB' },
  { value: '+33', label: '+33 FR' },
  { value: '+1', label: '+1 US/CA' },
  { value: '+49', label: '+49 DE' },
  { value: '+39', label: '+39 IT' },
  { value: '+other', label: 'Other' },
];

// Fields shown per lead source
// 'required' = shown and required, 'optional' = shown but optional, false/undefined = hidden
type FieldVisibility = 'required' | 'optional' | false;
interface SourceFieldConfig {
  firstName: FieldVisibility;
  lastName: FieldVisibility;
  email: FieldVisibility;
  phone: FieldVisibility;
  countryCode: FieldVisibility;
  instagramHandle: FieldVisibility;
  country: FieldVisibility;
  productCategory: FieldVisibility;
  budgetRange: FieldVisibility;
}

// CEO-defined required fields per lead source (Workscope v1.2 Section 1.1)
const SOURCE_FIELD_CONFIG: Record<string, SourceFieldConfig> = {
  whatsapp: {
    firstName: 'optional',
    lastName: 'optional',
    email: 'optional',
    phone: 'required',
    countryCode: 'required',
    instagramHandle: false,
    country: 'optional',
    productCategory: 'required',
    budgetRange: 'optional',
  },
  instagram: {
    firstName: 'optional',
    lastName: 'optional',
    email: 'optional',
    phone: 'required',
    countryCode: 'required',
    instagramHandle: 'required',
    country: 'optional',
    productCategory: 'required',
    budgetRange: 'optional',
  },
  klaviyo: {
    firstName: 'required',
    lastName: 'required',
    email: 'optional',
    phone: 'required',
    countryCode: 'required',
    instagramHandle: false,
    country: 'required',
    productCategory: 'required',
    budgetRange: 'optional',
  },
  email: {
    firstName: 'optional',
    lastName: 'optional',
    email: 'required',
    phone: 'required',
    countryCode: 'required',
    instagramHandle: false,
    country: 'optional',
    productCategory: 'required',
    budgetRange: 'optional',
  },
  phone_call: {
    firstName: 'required',
    lastName: 'required',
    email: 'optional',
    phone: 'required',
    countryCode: 'required',
    instagramHandle: false,
    country: 'optional',
    productCategory: 'required',
    budgetRange: 'optional',
  },
  walk_in: {
    firstName: 'required',
    lastName: 'required',
    email: 'optional',
    phone: 'required',
    countryCode: 'required',
    instagramHandle: false,
    country: 'required',
    productCategory: 'required',
    budgetRange: 'required',
  },
  referral: {
    firstName: 'required',
    lastName: 'required',
    email: 'optional',
    phone: 'required',
    countryCode: 'required',
    instagramHandle: false,
    country: 'required',
    productCategory: 'required',
    budgetRange: 'required',
  },
  website: {
    firstName: 'optional',
    lastName: 'optional',
    email: 'optional',
    phone: 'required',
    countryCode: 'required',
    instagramHandle: false,
    country: 'optional',
    productCategory: 'required',
    budgetRange: 'optional',
  },
  event: {
    firstName: 'optional',
    lastName: 'optional',
    email: 'optional',
    phone: 'required',
    countryCode: 'required',
    instagramHandle: false,
    country: 'optional',
    productCategory: 'required',
    budgetRange: 'optional',
  },
  other: {
    firstName: 'optional',
    lastName: 'optional',
    email: 'optional',
    phone: 'required',
    countryCode: 'required',
    instagramHandle: false,
    country: 'optional',
    productCategory: 'required',
    budgetRange: 'optional',
  },
};

const DEFAULT_FIELD_CONFIG: SourceFieldConfig = {
  firstName: 'optional',
  lastName: 'optional',
  email: 'optional',
  phone: 'required',
  countryCode: 'required',
  instagramHandle: false,
  country: 'optional',
  productCategory: 'required',
  budgetRange: 'optional',
};

const QuickCaptureModal: React.FC<QuickCaptureModalProps> = ({ open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const leadSource = Form.useWatch('leadSource', form);
  const [duplicateClient, setDuplicateClient] = useState<any>(null);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

  const fieldConfig: SourceFieldConfig = leadSource
    ? (SOURCE_FIELD_CONFIG[leadSource] ?? DEFAULT_FIELD_CONFIG)
    : DEFAULT_FIELD_CONFIG;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const payload: Record<string, any> = {
        leadSource: values.leadSource,
      };

      if (values.firstName) payload.firstName = values.firstName;
      if (values.lastName) payload.lastName = values.lastName;
      if (values.email) payload.email = values.email;
      if (values.instagramHandle) payload.instagramHandle = values.instagramHandle;
      if (values.country) payload.country = values.country;
      if (values.productCategory) payload.productCategory = values.productCategory;
      if (values.budgetRange) payload.budgetRange = values.budgetRange;

      // Combine countryCode prefix + phone number
      if (values.phone) {
        const prefix = values.countryCode && values.countryCode !== '+other' ? values.countryCode : '';
        const cleanedPhone = stripDuplicateCountryCode(values.phone || '', prefix);
        payload.phone = prefix ? `${prefix}${cleanedPhone}` : values.phone;
        payload.countryCode = values.countryCode || null;
      } else {
        // Phone is optional for some sources — provide a placeholder to satisfy DB if needed
        payload.phone = 'N/A';
      }

      const response = await crmCreateLead(payload);

      // D41: Handle existing client with same phone
      if (response?.existingClient === true) {
        setDuplicateClient(response.client);
        setDuplicateModalOpen(true);
        return;
      }

      // B12 fix: guard against missing client/deal in response
      if (!response?.client || !response?.deal) {
        message.error('Lead created but server returned incomplete data. Please refresh.');
        form.resetFields();
        onClose();
        return;
      }
      const isReturning = response?.deal?.isReturningLead === true;
      if (isReturning) {
        message.warning({
          content: `Lead created — ${response.client.leadCode}. This client has a previous closed deal. Marked as Returning Lead.`,
          duration: 8,
        });
      } else {
        message.success(`Lead created — ${response.client.leadCode}`);
      }
      form.resetFields();
      onSuccess(response.client.id, response.deal.id, isReturning);
    } catch (err: any) {
      if (err.errorFields) return; // validation error
      message.error(err.response?.data?.error || 'Failed to create lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleDuplicateConfirm = async () => {
    if (!duplicateClient) return;
    try {
      setSubmitting(true);
      const deal = await crmCreateDeal({ clientId: duplicateClient.id, title: `New Deal — ${duplicateClient.firstName || ''} ${duplicateClient.lastName || ''}`.trim() });
      message.success(`New deal created for existing client ${duplicateClient.firstName} ${duplicateClient.lastName}`);
      setDuplicateModalOpen(false);
      setDuplicateClient(null);
      form.resetFields();
      onSuccess(duplicateClient.id, deal.id);
      onClose();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to create deal for existing client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDuplicateCancel = () => {
    setDuplicateModalOpen(false);
    setDuplicateClient(null);
  };

  const isRequired = (field: keyof SourceFieldConfig) => fieldConfig[field] === 'required';
  const isVisible = (field: keyof SourceFieldConfig) => fieldConfig[field] !== false;

  const phoneAddonBefore = isVisible('countryCode') ? (
    <Form.Item name="countryCode" noStyle initialValue="+20">
      <Select
        style={{ width: 100 }}
        options={COUNTRY_CODE_OPTIONS}
        popupMatchSelectWidth={false}
      />
    </Form.Item>
  ) : undefined;

  return (
    <>
    <Modal
      open={open}
      title="New Lead — Quick Capture"
      width={540}
      onCancel={handleCancel}
      footer={
        <Space>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            Create Lead
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        {/* Lead Source — always first */}
        <Form.Item
          name="leadSource"
          label="Lead Source"
          rules={[{ required: true, message: 'Lead source is required' }]}
        >
          <Select
            placeholder="How did they find us?"
            options={LEAD_SOURCE_OPTIONS}
            onChange={() => {
              // Clear source-specific fields when source changes
              form.setFieldsValue({
                instagramHandle: undefined,
                email: undefined,
              });
            }}
          />
        </Form.Item>

        {/* Name row */}
        {(isVisible('firstName') || isVisible('lastName')) && (
          <Row gutter={12}>
            {isVisible('firstName') && (
              <Col span={12}>
                <Form.Item
                  name="firstName"
                  label="First Name"
                  rules={isRequired('firstName') ? [{ required: true, whitespace: true, message: 'First name required' }] : []}
                >
                  <Input placeholder="First name" />
                </Form.Item>
              </Col>
            )}
            {isVisible('lastName') && (
              <Col span={12}>
                <Form.Item
                  name="lastName"
                  label="Last Name"
                  rules={isRequired('lastName') ? [{ required: true, whitespace: true, message: 'Last name required' }] : []}
                >
                  <Input placeholder="Last name" />
                </Form.Item>
              </Col>
            )}
          </Row>
        )}

        {/* Instagram handle */}
        {isVisible('instagramHandle') && (
          <Form.Item
            name="instagramHandle"
            label="Instagram Handle"
            rules={isRequired('instagramHandle') ? [{ required: true, whitespace: true, message: 'Instagram handle required' }] : []}
          >
            <Input placeholder="@handle" />
          </Form.Item>
        )}

        {/* Phone */}
        {isVisible('phone') && (
          <Form.Item
            name="phone"
            label="Phone"
            rules={isRequired('phone') ? [{ required: true, whitespace: true, message: 'Phone is required' }] : []}
          >
            <Input
              addonBefore={phoneAddonBefore}
              placeholder="1xx xxx xxxx"
              style={{ width: '100%' }}
              onChange={(e) => {
                const val = e.target.value;
                if (val && val.startsWith('+')) {
                  const codes = COUNTRY_CODE_OPTIONS.map(c => c.value).filter(c => c !== '+other');
                  const detected = autoDetectCountryCode(val, codes);
                  if (detected) {
                    form.setFieldValue('countryCode', detected);
                    // Strip the code from the phone value after a short delay
                    setTimeout(() => {
                      form.setFieldValue('phone', val.substring(detected.length).trim());
                    }, 0);
                  }
                }
              }}
            />
          </Form.Item>
        )}

        {/* Email */}
        {isVisible('email') && (
          <Form.Item
            name="email"
            label="Email"
            rules={[
              ...(isRequired('email') ? [{ required: true, message: 'Email is required' }] : []),
              { type: 'email' as const, message: 'Enter a valid email address' },
            ]}
          >
            <Input placeholder="email@example.com" />
          </Form.Item>
        )}

        {/* Country */}
        {isVisible('country') && (
          <Form.Item
            name="country"
            label="Country"
            rules={isRequired('country') ? [{ required: true, message: 'Country is required' }] : []}
          >
            <Input placeholder="e.g. Egypt" />
          </Form.Item>
        )}

        {/* Product Category + Budget Range row */}
        <Row gutter={12}>
          {isVisible('productCategory') && (
            <Col span={12}>
              <Form.Item
                name="productCategory"
                label="Product Category"
                rules={isRequired('productCategory') ? [{ required: true, message: 'Product category required' }] : []}
              >
                <Select
                  placeholder="Category"
                  options={PRODUCT_CATEGORY_OPTIONS}
                  allowClear
                />
              </Form.Item>
            </Col>
          )}
          {isVisible('budgetRange') && (
            <Col span={isVisible('productCategory') ? 12 : 24}>
              <Form.Item
                name="budgetRange"
                label="Budget Range"
                rules={isRequired('budgetRange') ? [{ required: true, message: 'Budget range required' }] : []}
              >
                <Select
                  placeholder="Budget"
                  options={BUDGET_RANGE_OPTIONS}
                  allowClear
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>

    {/* D41: Duplicate phone confirmation modal */}
    <Modal
      open={duplicateModalOpen}
      title="Client Already Exists"
      onOk={handleDuplicateConfirm}
      onCancel={handleDuplicateCancel}
      confirmLoading={submitting}
      okText="Create New Deal"
      cancelText="Cancel"
    >
      {duplicateClient && (
        <p>
          A client with this phone already exists:{' '}
          <strong>{duplicateClient.firstName} {duplicateClient.lastName}</strong>
          {duplicateClient.leadCode && ` (${duplicateClient.leadCode})`}
          {duplicateClient.clientCode && ` / ${duplicateClient.clientCode}`}
          {duplicateClient.lifecycleStage && `, ${duplicateClient.lifecycleStage}`}.
          <br /><br />
          Would you like to create a new deal for this existing client?
        </p>
      )}
    </Modal>
    </>
  );
};

export default QuickCaptureModal;
